import { DateRange, LocationGroup, MapBounds, Vendor } from '../types/types';

const BOUNDS_CHANGE_THRESHOLD = 0.3; // Reduced from 0.5 for less cache misses

// Default center coordinates (fallback if geolocation fails)
export const DEFAULT_CENTER = { lng: -122.2658, lat: 37.8281 };
// export const DEFAULT_CENTER = { lng: 0, lat: 0 };
export const DEFAULT_ZOOM = 12;
export const MAX_ZOOM = 18;
// Zoom thresholds for marker visibility
export const ZOOM_THRESHOLDS = {
  HIDE_ALL_MARKERS: DEFAULT_ZOOM - 4, // Below this zoom, hide all markers (national scale)
  SHOW_CLUSTERED: DEFAULT_ZOOM - 2, // Between 8-10, could show clusters (future feature)
  SHOW_ALL_MARKERS: DEFAULT_ZOOM - 6, // Above this, show all markers
  DETAILED_VIEW: DEFAULT_ZOOM + 2, // Above this, show detailed markers with full info
};

// CRITICAL: Reduced delay to prevent multiple API calls on mobile
export const VIEWPORT_CHANGE_DELAY = 300; // Reduced from 1500ms

// Marker size configuration
export const MARKER_CONFIG = {
  baseSize: 35,
  scaleFactor: 0.9,
  maxScale: 3.5,
  zoomFactors: {
    low: 0.6,
    medium: 0.6,
    high: 1.2,
  },
};

export const hasBoundsChangedSignificantly = (
  oldBounds: MapBounds | null,
  newBounds: MapBounds | null
): boolean => {
  if (!oldBounds || !newBounds) return true;

  const latDiff = Math.abs(oldBounds.maxLat - oldBounds.minLat);
  const lngDiff = Math.abs(oldBounds.maxLng - oldBounds.minLng);

  // Avoid division by zero
  if (latDiff === 0 || lngDiff === 0) return true;

  const latChange = Math.abs(newBounds.maxLat - oldBounds.maxLat) / latDiff;
  const lngChange = Math.abs(newBounds.maxLng - oldBounds.maxLng) / lngDiff;

  return latChange > BOUNDS_CHANGE_THRESHOLD || lngChange > BOUNDS_CHANGE_THRESHOLD;
};

export const generateCacheKey = (bounds: MapBounds | null, dateRange: DateRange): string => {
  if (!bounds) return 'no-bounds';

  // Increased rounding precision to reduce cache key variations
  const roundedBounds = {
    minLng: Math.round(bounds.minLng * 100) / 100, // Reduced from 1000
    minLat: Math.round(bounds.minLat * 100) / 100,
    maxLng: Math.round(bounds.maxLng * 100) / 100,
    maxLat: Math.round(bounds.maxLat * 100) / 100,
  };

  const dateKey = dateRange
    ? `${dateRange.startDate?.toISOString().split('T')[0]}-${dateRange.endDate?.toISOString().split('T')[0]}`
    : 'no-date';

  return `${roundedBounds.minLng},${roundedBounds.minLat},${roundedBounds.maxLng},${roundedBounds.maxLat}-${dateKey}`;
};

export const dateRangesEqual = (range1: DateRange | null, range2: DateRange | null): boolean => {
  if (!range1 && !range2) return true;
  if (!range1 || !range2) return false;

  const start1 = range1.startDate?.getTime() || 0;
  const start2 = range2.startDate?.getTime() || 0;
  const end1 = range1.endDate?.getTime() || 0;
  const end2 = range2.endDate?.getTime() || 0;

  return start1 === start2 && end1 === end2;
};

// Memoization for groupVendorsByLocation
const groupVendorsCache = new Map<string, LocationGroup[]>();

export const groupVendorsByLocation = (vendors: Vendor[]): LocationGroup[] => {
  // Create cache key based on vendor IDs and coordinates
  const cacheKey = vendors
    .map(v => `${v.id}-${v.coordinates?.[0]}-${v.coordinates?.[1]}`)
    .sort()
    .join('|');

  // Return cached result if available
  if (groupVendorsCache.has(cacheKey)) {
    return groupVendorsCache.get(cacheKey)!;
  }

  const locationGroups: Record<string, Vendor[]> = {};

  vendors.forEach(vendor => {
    if (!vendor.coordinates || vendor.coordinates.length !== 2) return;

    const locationKey = `${vendor.coordinates[0].toFixed(6)},${vendor.coordinates[1].toFixed(6)}`;

    if (!locationGroups[locationKey]) {
      locationGroups[locationKey] = [];
    }
    locationGroups[locationKey].push(vendor);
  });

  const result = Object.entries(locationGroups).map(([locationKey, vendors]) => ({
    locationKey,
    coordinates: vendors[0].coordinates as [number, number],
    vendors,
    isColocated: vendors.length > 1,
  }));

  // Cache the result (limit cache size)
  if (groupVendorsCache.size > 50) {
    const firstKey = groupVendorsCache.keys().next().value;
    if (firstKey !== undefined) {
      groupVendorsCache.delete(firstKey);
    }
  }
  groupVendorsCache.set(cacheKey, result);

  return result;
};

export const addPulseStyles = () => {
  if (!document.getElementById('pulse-animation')) {
    const style = document.createElement('style');
    style.id = 'pulse-animation';
    style.textContent = `
      @keyframes pulse {
        0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.9; }
        70% { transform: translate(-50%, -50%) scale(1.6); opacity: 0.2; }
        100% { transform: translate(-50%, -50%) scale(1.8); opacity: 0; }
      }

      .pulse-ring {
        position: absolute;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: var(--color-location-ghost);
        animation: pulse 2s infinite ease-out;
        z-index: -1;
        top: 50%;
        left: 50%;
      }

      .pulse-dot {
        position: absolute;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: var(--color-location);
        z-index: -1;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    `;
    document.head.appendChild(style);
  }
};

// TODO: Varun this isn't actually filtering things by time; which is what we'd want.
export const getLatestEvent = (vendors: Vendor[]): Vendor => {
  const upcomingEvents = vendors.filter(vendor => {
    const eventDate = new Date(vendor.start_date || '');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate >= today;
  });

  return (
    upcomingEvents.sort(
      (a, b) => new Date(a.start_date || '').getTime() - new Date(b.start_date || '').getTime()
    )[0] || vendors[0]
  );
};

/**
 * Parses time string and converts to minutes since midnight for comparison
 * @param timeStr Time string in format "HH:MM" or "HHMM"
 * @returns Number of minutes since midnight
 */
export const parseTimeString = (timeStr: string): number => {
  const cleanedStr = timeStr.replace(':', '').padStart(4, '0');
  const hours = parseInt(cleanedStr.substring(0, 2), 10);
  const minutes = parseInt(cleanedStr.substring(2, 4), 10);
  return hours * 60 + minutes;
};

/**
 * Pure function returning marker visual styles.
 * Extracted from map.tsx getMarkerStyles useCallback — no React dependencies.
 *
 * @param isPopupOpen - Whether this marker's popup is currently open
 * @param isFree      - Whether this is a free / "just show up" event
 * @param zoom        - Current map zoom (reserved for future zoom-based sizing)
 */
export const getMarkerStyles = (
  isPopupOpen = false,
  isFree = false,
  _zoom?: number // accepted but unused until zoom-based sizing is implemented
): Record<string, string> => {
  const zoomMultiplier = MARKER_CONFIG.zoomFactors.medium;
  const size = Math.max(MARKER_CONFIG.baseSize * zoomMultiplier, 10);

  // selected/open state draws the eye with the pink accent, per docs/design-systems.md §5 —
  // this takes priority over the free-event yellow, which only applies when unselected
  const baseColor = isPopupOpen
    ? 'var(--color-accent)'
    : isFree
      ? 'var(--color-yellow)'
      : 'var(--color-primary)';
  const textColor = !isPopupOpen && isFree ? 'black' : 'white';

  return {
    width: `${size}px`,
    height: `${size}px`,
    background: baseColor,
    border: '2.5px solid white',
    borderRadius: '50%',
    boxShadow: isPopupOpen
      ? '0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(255, 255, 255, 0.3)'
      : '0 3px 10px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3)',
    cursor: 'pointer',
    transformOrigin: 'center center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: textColor,
    fontSize: `${Math.max(size * 0.4, 10)}px`,
    fontWeight: 'light',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  };
};

export function shouldShowMarkersAtZoom(zoom: number): boolean {
  return zoom >= ZOOM_THRESHOLDS.HIDE_ALL_MARKERS;
}

export function isMarkerInViewport(
  markerLng: number,
  markerLat: number,
  bounds: { north: number; south: number; east: number; west: number }
): boolean {
  return (
    markerLat <= bounds.north &&
    markerLat >= bounds.south &&
    markerLng <= bounds.east &&
    markerLng >= bounds.west
  );
}
